<template>
  <div class="compare-mode-container">
    <!-- List view header -->
    <div class="page-header" v-if="!currentComparison">
      <div class="page-header-top">
        <h2>Compare Mode</h2>
        <button @click="createComparison" class="action-button primary create-btn">
          <i class="fas fa-plus"></i> Create Comparison
        </button>
      </div>
      <p>Line up two rides at the gate and play them back in sync.</p>
    </div>

    <!-- Existing Comparisons List -->
    <div v-if="comparisons.length > 0 && !currentComparison" class="comparisons-list">
      <div class="comparison-card" v-for="comparison in comparisons" :key="comparison.id" @click="selectComparison(comparison)">
        <div class="comparison-info">
          <h4>{{ comparison.name }}</h4>
          <p>{{ formatDate(comparison.created) }}</p>
          <div class="video-status">
            <span class="status-badge" :class="{ 'complete': comparison.video1 }">
              <i :class="comparison.video1 ? 'fas fa-check' : 'fas fa-circle-notch'"></i> Video 1
            </span>
            <span class="status-badge" :class="{ 'complete': comparison.video2 }">
              <i :class="comparison.video2 ? 'fas fa-check' : 'fas fa-circle-notch'"></i> Video 2
            </span>
          </div>
        </div>
        <div class="comparison-actions">
          <button @click.stop="selectComparison(comparison)" class="action-button">Open</button>
          <button @click.stop="deleteComparison(comparison.id)" class="action-button danger ghost" title="Delete">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="comparisons.length === 0 && !currentComparison && !loading" class="empty-state">
      <i class="fas fa-clone"></i>
      <p>No comparisons yet. Create one to line up two rides side by side.</p>
    </div>

    <!-- Current Comparison Interface -->
    <div v-if="currentComparison" class="comparison-interface" :data-synced="syncConfirmed">
      <div class="comparison-header">
        <button @click="backToList" class="back-button"><i class="fas fa-arrow-left"></i> Back</button>
        <input
          class="comparison-title-input"
          v-model="currentComparison.name"
          @change="updateName"
          @blur="updateName"
          placeholder="Name this comparison"
        />
      </div>

      <!-- View mode toggle (synced) -->
      <div v-if="syncConfirmed" class="view-mode-bar">
        <div class="segmented">
          <button :class="{ active: viewMode === 'side-by-side' }" @click="setViewMode('side-by-side')">
            <i class="fas fa-table-columns"></i> Side by side
          </button>
          <button :class="{ active: viewMode === 'overlay' }" @click="setViewMode('overlay')">
            <i class="fas fa-clone"></i> Overlay
          </button>
        </div>
        <div v-if="viewMode === 'overlay'" class="blend-control">
          <label>Blend</label>
          <input type="range" min="0.1" max="0.9" step="0.05" v-model.number="overlayOpacity" />
        </div>
      </div>

      <!-- Video Upload Section -->
      <div
        class="video-upload-section"
        :class="{ 'overlay-mode': viewMode === 'overlay' && syncConfirmed }"
        :style="{ '--overlay-opacity': overlayOpacity }"
      >
        <div class="video-upload-card">
          <h4 v-if="!syncConfirmed">Video 1</h4>
          <div v-if="!currentComparison.video1" class="upload-area">
            <input 
              type="file" 
              ref="video1Input"
              @change="handleVideoSelect($event, 1)"
              accept="video/*"
              style="display: none"
            />
            <div class="upload-placeholder" @click="$refs.video1Input.click()">
              <i class="fas fa-cloud-upload-alt"></i>
              <p>Click to upload Video 1</p>
            </div>
            <div class="upload-or">or</div>
            <button class="action-button secondary select-app-btn" @click="openPicker(1)">
              <i class="fas fa-photo-film"></i> Select from 6buoys
            </button>
            <div v-if="uploadProgress[1] > 0" class="upload-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: uploadProgress[1] + '%' }"></div>
              </div>
              <p>{{ uploadStatusLabel(1) }} {{ uploadProgress[1] }}%</p>
            </div>
          </div>
          <div v-else class="video-uploaded">
            <button
              v-if="!syncConfirmed"
              class="remove-video-btn"
              @click.stop="removeVideo(1)"
              title="Remove video"
            >
              <i class="fas fa-times"></i>
            </button>
            <div class="video-frame" :class="{ 'is-zoomed': isZoomed(1) }">
              <video 
                :ref="`video1Player`"
                :src="getVideoUrl(currentComparison[`video1_cdn_url`] || currentComparison[`video1`])"
                class="uploaded-video"
                :class="{ 'square-crop': isSquare(1), 'zoomed': isZoomed(1) }"
                :style="videoStyle(1)"
                @loadedmetadata="onIndividualVideoLoaded(1)"
                @timeupdate="onVideoTimeUpdate(1)"
                :playsinline="true"
                :controls="false"
                @click.prevent="handleVideoClick(1)"
              ></video>
            </div>

            <!-- Square Crop Editor for Video 1 -->
            <div v-if="cropEditing === 1" class="crop-editor">
              <p class="crop-hint">Vertical video detected. Position the skier in the center of the square, then confirm.</p>
              <div class="crop-slider-row">
                <span>Top</span>
                <input type="range" min="0" max="100" step="1" v-model.number="cropDraft" class="crop-slider" />
                <span>Bottom</span>
              </div>
              <div class="crop-actions">
                <button @click="confirmCrop" class="action-button primary small">Confirm crop</button>
                <button @click="cancelCrop" class="action-button secondary small">Use full video</button>
              </div>
            </div>

            <!-- Zoom Editor for Video 1 -->
            <div v-if="zoomEditing === 1" class="crop-editor">
              <p class="crop-hint">Zoom in and position the skier, then confirm.</p>
              <div class="crop-slider-row">
                <span><i class="fas fa-magnifying-glass"></i></span>
                <input type="range" min="1" max="3" step="0.05" v-model.number="zoomDraft" class="crop-slider" />
                <span>{{ zoomDraft.toFixed(2) }}x</span>
              </div>
              <div class="crop-slider-row">
                <span>Left</span>
                <input type="range" min="0" max="100" step="1" v-model.number="zoomXDraft" class="crop-slider" />
                <span>Right</span>
              </div>
              <div class="crop-slider-row">
                <span>Top</span>
                <input type="range" min="0" max="100" step="1" v-model.number="zoomYDraft" class="crop-slider" />
                <span>Bottom</span>
              </div>
              <div class="crop-actions">
                <button @click="confirmZoom" class="action-button primary small">Confirm zoom</button>
                <button v-if="isZoomed(1)" @click="removeZoom" class="action-button secondary small">Remove zoom</button>
                <button v-else @click="cancelZoom" class="action-button secondary small">Cancel</button>
              </div>
            </div>

            <button
              v-if="isPortrait[1] && cropEditing !== 1 && !syncConfirmed"
              @click="editCrop(1)"
              class="action-button secondary small crop-toggle"
            >
              <i class="fas fa-crop-simple"></i> Adjust crop
            </button>
            <button
              v-if="!isPortrait[1] && zoomEditing !== 1 && !syncConfirmed && videosLoaded[1]"
              @click="editZoom(1)"
              class="action-button secondary small crop-toggle"
            >
              <i class="fas fa-magnifying-glass-plus"></i> {{ isZoomed(1) ? 'Adjust zoom' : 'Zoom in' }}
            </button>
            
            <!-- Scrub + gate controls for Video 1 -->
            <div v-if="!syncConfirmed" class="gate-controls">
              <div class="scrub-row">
                <button @click="toggleIndividualPlay(1)" class="scrub-play">
                  <i :class="individualVideoStates[1]?.isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
                </button>
                <span class="scrub-time">{{ formatTime(individualVideoStates[1]?.currentTime || 0) }}</span>
                <input 
                  type="range"
                  min="0"
                  :max="individualVideoStates[1]?.duration || 0"
                  step="0.1"
                  :value="individualVideoStates[1]?.currentTime || 0"
                  @input="seekIndividualVideo(1, $event.target.value)"
                  class="scrub-slider"
                  :disabled="!individualVideoStates[1]?.duration"
                />
              </div>
              <div class="gate-row" :class="{ 'is-set': currentComparison.video1_gate_time }">
                <span class="gate-label">
                  <i class="fas fa-flag-checkered"></i> Gate time
                </span>
                <div class="gate-value">
                  <input 
                    v-model.number="currentComparison.video1_gate_time"
                    type="number" 
                    step="0.001"
                    placeholder="0.000"
                    @input="updateGateTime(1, $event.target.value)"
                  />
                  <span class="gate-unit">s</span>
                </div>
                <button @click="setCurrentTimeAsGate(1)" class="set-gate-btn">
                  Set to current frame
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="video-upload-card">
          <h4 v-if="!syncConfirmed">Video 2</h4>
          <div v-if="!currentComparison.video2" class="upload-area">
            <input 
              type="file" 
              ref="video2Input"
              @change="handleVideoSelect($event, 2)"
              accept="video/*"
              style="display: none"
            />
            <div class="upload-placeholder" @click="$refs.video2Input.click()">
              <i class="fas fa-cloud-upload-alt"></i>
              <p>Click to upload Video 2</p>
            </div>
            <div class="upload-or">or</div>
            <button class="action-button secondary select-app-btn" @click="openPicker(2)">
              <i class="fas fa-photo-film"></i> Select from 6buoys
            </button>
            <div v-if="uploadProgress[2] > 0" class="upload-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: uploadProgress[2] + '%' }"></div>
              </div>
              <p>{{ uploadStatusLabel(2) }} {{ uploadProgress[2] }}%</p>
            </div>
          </div>
          <div v-else class="video-uploaded">
            <button
              v-if="!syncConfirmed"
              class="remove-video-btn"
              @click.stop="removeVideo(2)"
              title="Remove video"
            >
              <i class="fas fa-times"></i>
            </button>
            <div class="video-frame" :class="{ 'is-zoomed': isZoomed(2) }">
              <video 
                :ref="`video2Player`"
                :src="getVideoUrl(currentComparison[`video2_cdn_url`] || currentComparison[`video2`])"
                class="uploaded-video"
                :class="{ 'square-crop': isSquare(2), 'zoomed': isZoomed(2) }"
                :style="videoStyle(2)"
                @loadedmetadata="onIndividualVideoLoaded(2)"
                @timeupdate="onVideoTimeUpdate(2)"
                :playsinline="true"
                :controls="false"
                @click.prevent="handleVideoClick(2)"
              ></video>
            </div>

            <!-- Square Crop Editor for Video 2 -->
            <div v-if="cropEditing === 2" class="crop-editor">
              <p class="crop-hint">Vertical video detected. Position the skier in the center of the square, then confirm.</p>
              <div class="crop-slider-row">
                <span>Top</span>
                <input type="range" min="0" max="100" step="1" v-model.number="cropDraft" class="crop-slider" />
                <span>Bottom</span>
              </div>
              <div class="crop-actions">
                <button @click="confirmCrop" class="action-button primary small">Confirm crop</button>
                <button @click="cancelCrop" class="action-button secondary small">Use full video</button>
              </div>
            </div>

            <!-- Zoom Editor for Video 2 -->
            <div v-if="zoomEditing === 2" class="crop-editor">
              <p class="crop-hint">Zoom in and position the skier, then confirm.</p>
              <div class="crop-slider-row">
                <span><i class="fas fa-magnifying-glass"></i></span>
                <input type="range" min="1" max="3" step="0.05" v-model.number="zoomDraft" class="crop-slider" />
                <span>{{ zoomDraft.toFixed(2) }}x</span>
              </div>
              <div class="crop-slider-row">
                <span>Left</span>
                <input type="range" min="0" max="100" step="1" v-model.number="zoomXDraft" class="crop-slider" />
                <span>Right</span>
              </div>
              <div class="crop-slider-row">
                <span>Top</span>
                <input type="range" min="0" max="100" step="1" v-model.number="zoomYDraft" class="crop-slider" />
                <span>Bottom</span>
              </div>
              <div class="crop-actions">
                <button @click="confirmZoom" class="action-button primary small">Confirm zoom</button>
                <button v-if="isZoomed(2)" @click="removeZoom" class="action-button secondary small">Remove zoom</button>
                <button v-else @click="cancelZoom" class="action-button secondary small">Cancel</button>
              </div>
            </div>

            <button
              v-if="isPortrait[2] && cropEditing !== 2 && !syncConfirmed"
              @click="editCrop(2)"
              class="action-button secondary small crop-toggle"
            >
              <i class="fas fa-crop-simple"></i> Adjust crop
            </button>
            <button
              v-if="!isPortrait[2] && zoomEditing !== 2 && !syncConfirmed && videosLoaded[2]"
              @click="editZoom(2)"
              class="action-button secondary small crop-toggle"
            >
              <i class="fas fa-magnifying-glass-plus"></i> {{ isZoomed(2) ? 'Adjust zoom' : 'Zoom in' }}
            </button>
            
            <!-- Scrub + gate controls for Video 2 -->
            <div v-if="!syncConfirmed" class="gate-controls">
              <div class="scrub-row">
                <button @click="toggleIndividualPlay(2)" class="scrub-play">
                  <i :class="individualVideoStates[2]?.isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
                </button>
                <span class="scrub-time">{{ formatTime(individualVideoStates[2]?.currentTime || 0) }}</span>
                <input 
                  type="range"
                  min="0"
                  :max="individualVideoStates[2]?.duration || 0"
                  step="0.1"
                  :value="individualVideoStates[2]?.currentTime || 0"
                  @input="seekIndividualVideo(2, $event.target.value)"
                  class="scrub-slider"
                  :disabled="!individualVideoStates[2]?.duration"
                />
              </div>
              <div class="gate-row" :class="{ 'is-set': currentComparison.video2_gate_time }">
                <span class="gate-label">
                  <i class="fas fa-flag-checkered"></i> Gate time
                </span>
                <div class="gate-value">
                  <input 
                    v-model.number="currentComparison.video2_gate_time"
                    type="number" 
                    step="0.001"
                    placeholder="0.000"
                    @input="updateGateTime(2, $event.target.value)"
                  />
                  <span class="gate-unit">s</span>
                </div>
                <button @click="setCurrentTimeAsGate(2)" class="set-gate-btn">
                  Set to current frame
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky Sync Action Bar -->
      <div v-if="currentComparison.video1 && currentComparison.video2 && !syncConfirmed" class="sync-bar">
        <div class="sync-bar-gates">
          <span class="sync-gate" :class="{ 'set': currentComparison.video1_gate_time }">
            V1 gate <strong>{{ currentComparison.video1_gate_time ? Number(currentComparison.video1_gate_time).toFixed(3) + 's' : '—' }}</strong>
          </span>
          <span class="sync-gate" :class="{ 'set': currentComparison.video2_gate_time }">
            V2 gate <strong>{{ currentComparison.video2_gate_time ? Number(currentComparison.video2_gate_time).toFixed(3) + 's' : '—' }}</strong>
          </span>
        </div>
        <button 
          @click="confirmSync" 
          class="action-button primary sync-btn"
          :disabled="!currentComparison.video1_gate_time || !currentComparison.video2_gate_time"
        >
          <i class="fas fa-wand-magic-sparkles"></i>
          {{ (!currentComparison.video1_gate_time || !currentComparison.video2_gate_time) ? 'Set both gate times' : 'Sync videos' }}
        </button>
      </div>

      <!-- Synchronized Controls Section -->
      <div v-if="currentComparison.video1 && currentComparison.video2 && syncConfirmed" class="controls-section">
        <!-- Loading indicator when videos are not ready -->
        <div v-if="!videosLoaded[1] || !videosLoaded[2]" class="loading-indicator">
          <div class="loading-spinner"></div>
          <div class="loading-status">
            <span :class="{ 'loaded': videosLoaded[1] }">Video 1: {{ videosLoaded[1] ? 'Ready' : 'Loading...' }}</span>
            <span :class="{ 'loaded': videosLoaded[2] }">Video 2: {{ videosLoaded[2] ? 'Ready' : 'Loading...' }}</span>
          </div>
        </div>
        
        <div class="video-controls" :class="{ 'disabled': !videosLoaded[1] || !videosLoaded[2] }">
          <button @click="togglePlay" class="control-button play-pause" :disabled="!videosLoaded[1] || !videosLoaded[2]">
            <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
          </button>
          
          <div class="time-display">
            {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
          </div>
          
          <div class="progress-container">
            <input 
              type="range"
              min="0"
              :max="duration"
              step="0.1"
              v-model="currentTime"
              @input="seekTo"
              class="progress-slider"
              :disabled="!videosLoaded[1] || !videosLoaded[2]"
            />
          </div>
          
          <div class="speed-control">
            <label>Speed</label>
            <select v-model="playbackRate" @change="setPlaybackRate" class="speed-select" :disabled="!videosLoaded[1] || !videosLoaded[2]">
              <option value="0.1">0.1x</option>
              <option value="0.25">0.25x</option>
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>
          </div>
        </div>

        <div class="sync-info" v-if="syncInstructions">
          <p>
            <span v-if="syncInstructions.delay_video">
              Video {{ syncInstructions.delay_video }} delayed by {{ syncInstructions.delay_seconds.toFixed(3) }}s to align the gates
            </span>
            <span v-else>Videos are already in sync</span>
          </p>
          <button @click="resetSync" class="action-button secondary small">Adjust gate times</button>
        </div>
      </div>
    </div>

    <!-- Select-from-app Video Picker -->
    <div v-if="picker.active" class="picker-overlay" @click.self="closePicker">
      <div class="picker-modal">
        <div class="picker-header">
          <h3>Select a video for slot {{ picker.videoNumber }}</h3>
          <button class="picker-close" @click="closePicker"><i class="fas fa-times"></i></button>
        </div>
        <div class="picker-filters">
          <div class="picker-source-toggle">
            <button
              type="button"
              class="picker-source-btn"
              :class="{ 'active': !picker.filters.mine }"
              @click="setPickerSource(false)"
            >All Posts</button>
            <button
              type="button"
              class="picker-source-btn"
              :class="{ 'active': picker.filters.mine }"
              @click="setPickerSource(true)"
            >My Posts</button>
          </div>
          <select v-model="picker.filters.minSpeed" @change="applyPickerFilters" class="form-input small">
            <option value="">All Speeds</option>
            <option v-for="s in speedOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
          <select v-model="picker.filters.minRope" @change="applyPickerFilters" class="form-input small">
            <option value="">All Rope Lengths</option>
            <option v-for="r in ropeOptions" :key="r.value" :value="r.value">{{ r.label }}</option>
          </select>
        </div>
        <div class="picker-body">
          <div class="picker-grid">
            <div
              v-for="post in picker.posts"
              :key="post.id"
              class="picker-item"
              @click="selectPostVideo(post)"
              @mouseenter="hoverPlay($event)"
              @mouseleave="hoverPause($event)"
            >
              <video
                :src="getVideoUrl(post.video)"
                class="picker-video"
                muted
                loop
                preload="metadata"
                playsinline
                @loadedmetadata="onPickerVideoMeta(post.id, $event)"
              ></video>
              <div class="picker-play-hint"><i class="fas fa-play"></i></div>
              <div class="picker-author" v-if="authorName(post)">
                <i class="fas fa-user"></i> {{ authorName(post) }}
              </div>
              <div class="picker-meta">
                <span v-if="pickerOrientation[post.id]" class="picker-badge">
                  <i :class="pickerOrientation[post.id] === 'vertical' ? 'fas fa-mobile-screen' : 'fas fa-tv'"></i>
                  {{ pickerOrientation[post.id] === 'vertical' ? 'Vertical' : 'Horizontal' }}
                </span>
                <span v-if="post.rope" class="picker-badge">{{ post.rope }}m</span>
                <span v-if="post.speed" class="picker-badge">{{ post.speed }}kph</span>
              </div>
            </div>
          </div>
          <div v-if="picker.loading" class="picker-status">Loading…</div>
          <div v-if="!picker.loading && picker.posts.length === 0" class="picker-status">No videos found.</div>
          <button
            v-if="picker.hasMore && picker.posts.length > 0"
            class="action-button secondary picker-more"
            :disabled="picker.loading"
            @click="fetchPickerPosts"
          >
            {{ picker.loading ? 'Loading…' : 'Show more' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import {
  uploadVideo,
  describeUploadError,
  PHASE_PROCESSING,
} from '@/services/videoUploadService';
import axios from 'axios';

export default {
  name: 'CompareMode',
  data() {
    return {
      loading: false,
      comparisons: [],
      currentComparison: null,
      uploadProgress: { 1: 0, 2: 0 },
      uploadPhase: { 1: null, 2: null },
      uploading: { 1: false, 2: false },
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      playbackRate: 1,
      syncInstructions: null,
      videosLoaded: { 1: false, 2: false },
      syncConfirmed: false,
      individualVideoStates: { 
        1: { isPlaying: false, currentTime: 0, duration: 0 }, 
        2: { isPlaying: false, currentTime: 0, duration: 0 } 
      },
      // Square-crop state for vertical videos (display-only)
      isPortrait: { 1: false, 2: false },
      pendingCropCheck: { 1: false, 2: false },
      cropEditing: null,
      cropDraft: 50,
      // Zoom state for horizontal videos (display-only)
      zoomEditing: null,
      zoomDraft: 1.5,
      zoomXDraft: 50,
      zoomYDraft: 50,
      // View mode for synced playback: side-by-side or overlay (ghost) comparison
      viewMode: 'side-by-side',
      overlayOpacity: 0.5,
      // In-app video picker
      picker: {
        active: false,
        videoNumber: null,
        posts: [],
        loading: false,
        offset: 0,
        limit: 12,
        hasMore: true,
        filters: { minSpeed: '', minRope: '', mine: false },
      },
      pickerOrientation: {},
      speedOptions: [
        { value: '58', label: '58kph - 36mph' },
        { value: '55', label: '55kph - 34mph' },
        { value: '52', label: '52kph - 32mph' },
        { value: '49', label: '49kph - 30mph' },
        { value: '46', label: '46kph - 28mph' },
        { value: '43', label: '43kph - 26mph' },
        { value: '40', label: '40kph - 25mph' },
        { value: '37', label: '37kph - 23mph' }
      ],
      ropeOptions: [
        { value: '18', label: '18.25 meter - 15 Off' },
        { value: '16', label: '16.00 meter - 22 Off' },
        { value: '14', label: '14.25 meter - 28 Off' },
        { value: '13', label: '13.00 meter - 32 Off' },
        { value: '12', label: '12.00 meter - 35 Off' },
        { value: '11', label: '11.25 meter - 38 Off' },
        { value: '10.75', label: '10.75 meter - 39.5 Off' },
        { value: '10.25', label: '10.25 meter - 41 Off' },
        { value: '9', label: '9.75 meter - 43 Off' }
      ],
    };
  },
  async created() {
    await this.fetchComparisons();
  },
  methods: {
    async fetchComparisons() {
      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/api/comparisons/`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        this.comparisons = response.data;
      } catch (error) {
        console.error('Error fetching comparisons:', error);
        alert('Failed to load comparisons');
      } finally {
        this.loading = false;
      }
    },

    async createComparison() {
      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const defaultName = `Comparison ${this.formatDate(new Date().toISOString())}`;
        const response = await axios.post(`${API_BASE_URL}/api/comparisons/create/`, {
          name: defaultName
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
        this.syncConfirmed = false;
        this.viewMode = 'side-by-side';
        await this.fetchComparisons();
      } catch (error) {
        console.error('Error creating comparison:', error);
        alert('Failed to create comparison');
      } finally {
        this.loading = false;
      }
    },

    async updateName() {
      if (!this.currentComparison) return;
      const name = (this.currentComparison.name || '').trim() || 'Untitled comparison';
      this.currentComparison.name = name;
      try {
        const token = localStorage.getItem('access_token');
        await axios.patch(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/update/`,
          { name },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Error updating name:', error);
      }
    },

    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.$nextTick(() => {
        if (this.videosLoaded[1]) this.applyVideoSizing(1, this.$refs.video1Player);
        if (this.videosLoaded[2]) this.applyVideoSizing(2, this.$refs.video2Player);
      });
    },

    selectComparison(comparison) {
      this.currentComparison = comparison;
      this.syncInstructions = comparison.sync_instructions;
      
      // Auto-sync if both gate times are already set
      if (comparison.video1_gate_time && comparison.video2_gate_time) {
        this.syncConfirmed = true;
      } else {
        this.syncConfirmed = false;
      }
      
      // Initialize individual video states for existing videos
      this.$nextTick(() => {
        const video1 = this.$refs.video1Player;
        const video2 = this.$refs.video2Player;
        
        if (video1 && video1.duration) {
          this.individualVideoStates[1] = {
            isPlaying: false,
            currentTime: video1.currentTime || 0,
            duration: video1.duration
          };
        }
        
        if (video2 && video2.duration) {
          this.individualVideoStates[2] = {
            isPlaying: false,
            currentTime: video2.currentTime || 0,
            duration: video2.duration
          };
        }
        
        console.log('Selected comparison - individual video states:', this.individualVideoStates);
      });
    },

    backToList() {
      this.currentComparison = null;
      this.syncInstructions = null;
      this.syncConfirmed = false;
      this.resetVideoPlayers();
      
      // Refresh the comparisons list to show any updates
      this.fetchComparisons();
    },

    async deleteComparison(id) {
      if (!confirm('Are you sure you want to delete this comparison?')) return;
      
      try {
        const token = localStorage.getItem('access_token');
        await axios.delete(`${API_BASE_URL}/api/comparisons/${id}/delete/`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        await this.fetchComparisons();
        if (this.currentComparison && this.currentComparison.id === id) {
          this.currentComparison = null;
        }
      } catch (error) {
        console.error('Error deleting comparison:', error);
        alert('Failed to delete comparison');
      }
    },

    async handleVideoSelect(event, videoNumber) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file) return;

      const valid = await this.validateVideoFile(file);
      if (!valid) return;

      await this.uploadVideo(file, videoNumber);
    },

    validateVideoFile(file) {
      const MAX_FILE_SIZE = 300 * 1024 * 1024; // 300MB
      if (file.size > MAX_FILE_SIZE) {
        alert('File is too large. Please upload a video smaller than 300MB.');
        return Promise.resolve(false);
      }

      return new Promise((resolve) => {
        const videoElement = document.createElement('video');
        videoElement.preload = 'metadata';
        videoElement.onloadedmetadata = () => {
          URL.revokeObjectURL(videoElement.src);
          if (videoElement.duration > 60) {
            alert('Please upload a video shorter than 1 minute.');
            resolve(false);
          } else {
            resolve(true);
          }
        };
        videoElement.onerror = () => {
          URL.revokeObjectURL(videoElement.src);
          resolve(true); // let the server validate if metadata can't be read
        };
        videoElement.src = URL.createObjectURL(file);
      });
    },

    async uploadVideo(file, videoNumber) {
      if (this.uploading[videoNumber]) return;

      this.uploading[videoNumber] = true;
      this.uploadProgress[videoNumber] = 0;
      this.uploadPhase[videoNumber] = null;

      try {
        const result = await uploadVideo({
          file,
          endpoint: '/api/comparisons/upload-chunk/',
          extraFields: { video_number: videoNumber },
          onProgress: (value) => { this.uploadProgress[videoNumber] = value; },
          onPhase: (phase) => { this.uploadPhase[videoNumber] = phase; },
        });

        await this.attachVideoToComparison(result.video_path, videoNumber);
      } catch (error) {
        console.error('Error uploading video:', error.response || error);
        alert(describeUploadError(error));
        this.uploadProgress[videoNumber] = 0;
        this.uploadPhase[videoNumber] = null;
      } finally {
        this.uploading[videoNumber] = false;
      }
    },

    uploadStatusLabel(videoNumber) {
      if (this.uploadPhase[videoNumber] === PHASE_PROCESSING) return 'Processing video…';
      return 'Uploading…';
    },

    async attachVideoToComparison(videoPath, videoNumber) {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/attach-video/`,
          {
            video_path: videoPath,
            video_number: videoNumber.toString()
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
        // A fresh video was attached: re-check orientation and offer cropping.
        this.pendingCropCheck[videoNumber] = true;
        this.videosLoaded[videoNumber] = false;
      } catch (error) {
        console.error('Error attaching video:', error);
        alert('Failed to attach video to comparison');
      }
    },

    async removeVideo(videoNumber) {
      if (!this.currentComparison) return;
      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/remove-video/`,
          { video_number: videoNumber.toString() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
        // Reset per-slot editing/loading state for the cleared slot.
        this.videosLoaded[videoNumber] = false;
        this.isPortrait[videoNumber] = false;
        if (this.cropEditing === videoNumber) this.cropEditing = null;
        if (this.zoomEditing === videoNumber) this.zoomEditing = null;
      } catch (error) {
        console.error('Error removing video:', error);
        alert('Failed to remove video');
      } finally {
        this.loading = false;
      }
    },

    async updateGateTime(videoNumber, value) {
      if (!this.currentComparison) return;
      
      try {
        const token = localStorage.getItem('access_token');
        const updateData = {};
        
        if (videoNumber === 1) {
          updateData.video1_gate_time = value || null;
        } else {
          updateData.video2_gate_time = value || null;
        }

        const response = await axios.patch(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/update/`,
          updateData,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        this.currentComparison = response.data;
        
        // Always update sync instructions when gate times change
        this.syncInstructions = response.data.sync_instructions;
        
        console.log('Gate time updated, new sync instructions:', this.syncInstructions);
      } catch (error) {
        console.error('Error updating gate time:', error);
      }
    },

    getVideoUrl(url) {
      if (!url) return '';
      // If it's already a full URL, return as is
      if (url.startsWith('http')) return url;
      // Otherwise, assume it's a relative path from S3
      return `${API_BASE_URL}${url}`;
    },

    onIndividualVideoLoaded(videoNumber) {
      console.log(`Video ${videoNumber} loaded`);
      this.videosLoaded[videoNumber] = true;
      
      // Set duration for individual video controls
      const video = this.$refs[`video${videoNumber}Player`];
      if (video) {
        this.individualVideoStates[videoNumber].duration = video.duration;
        this.isPortrait[videoNumber] = video.videoHeight > video.videoWidth;

        // Offer the square-crop step once for a newly-attached portrait video.
        if (this.pendingCropCheck[videoNumber]) {
          this.pendingCropCheck[videoNumber] = false;
          this.maybeStartCrop(videoNumber);
        }

        this.applyVideoSizing(videoNumber, video);
      }
      
      // Check if both videos are now loaded for synchronized playback
      if (this.videosLoaded[1] && this.videosLoaded[2]) {
        console.log('Both videos loaded - synchronized playback ready');
        // Both videos loaded, set duration to the shorter one for sync controls
        const video1 = this.$refs.video1Player;
        const video2 = this.$refs.video2Player;
        this.duration = Math.min(video1.duration, video2.duration);
        
        // If we were trying to play but waiting for loading, we can now proceed
        if (this.syncConfirmed && this.isPlaying) {
          console.log('Resuming playback now that both videos are loaded');
          this.playActiveVideos();
        }
      }
    },

    onVideoTimeUpdate(videoNumber) {
      const video = this.$refs[`video${videoNumber}Player`];
      if (!video) return;
      
      // Update individual video state
      if (this.individualVideoStates[videoNumber]) {
        this.individualVideoStates[videoNumber].currentTime = video.currentTime;
      }
      
      // Update synced currentTime if we're in sync mode and playing
      if (this.syncConfirmed && this.isPlaying) {
        // Use the reference video (the one without delay) for time tracking
        if (this.syncInstructions && this.syncInstructions.delay_video === 1 && videoNumber === 2) {
          this.currentTime = video.currentTime;
        } else if ((!this.syncInstructions || this.syncInstructions.delay_video !== 1) && videoNumber === 1) {
          this.currentTime = video.currentTime;
        }
        
        // Check if delayed video should start playing
        if (this.syncInstructions && this.syncInstructions.delay_video) {
          const delaySeconds = this.syncInstructions.delay_seconds;
          const video1 = this.$refs.video1Player;
          const video2 = this.$refs.video2Player;
          
          if (this.syncInstructions.delay_video === 1 && this.currentTime >= delaySeconds) {
            // Video 1 should start if it's not already playing
            if (video1 && video1.paused) {
              video1.currentTime = this.currentTime - delaySeconds;
              video1.play();
            }
          } else if (this.syncInstructions.delay_video === 2 && this.currentTime >= delaySeconds) {
            // Video 2 should start if it's not already playing
            if (video2 && video2.paused) {
              video2.currentTime = this.currentTime - delaySeconds;
              video2.play();
            }
          }
        }
      }
    },

    toggleIndividualPlay(videoNumber) {
      const video = this.$refs[`video${videoNumber}Player`];
      if (video) {
        if (this.individualVideoStates[videoNumber].isPlaying) {
          video.pause();
        } else {
          video.play();
        }
        this.individualVideoStates[videoNumber].isPlaying = !this.individualVideoStates[videoNumber].isPlaying;
      }
    },

    seekIndividualVideo(videoNumber, value) {
      console.log(`SeekIndividualVideo called: video ${videoNumber}, value: ${value}`);
      const video = this.$refs[`video${videoNumber}Player`];
      console.log(`Video ref found:`, video);
      
      if (video) {
        const time = parseFloat(value);
        console.log(`Setting video ${videoNumber} currentTime to ${time}`);
        video.currentTime = time;
        
        // Ensure the state is updated
        if (!this.individualVideoStates[videoNumber]) {
          this.individualVideoStates[videoNumber] = { isPlaying: false, currentTime: 0, duration: 0 };
        }
        this.individualVideoStates[videoNumber].currentTime = time;
        console.log(`Updated state for video ${videoNumber}:`, this.individualVideoStates[videoNumber]);
      } else {
        console.log(`Video ${videoNumber} ref not found`);
        console.log('Available refs:', Object.keys(this.$refs));
      }
    },

    setCurrentTimeAsGate(videoNumber) {
      if (this.currentComparison) {
        const video = this.$refs[`video${videoNumber}Player`];
        if (video) {
          const currentTime = video.currentTime.toFixed(3);
          // Update the gate time input field and trigger the update
          if (videoNumber === 1) {
            this.currentComparison.video1_gate_time = currentTime;
          } else {
            this.currentComparison.video2_gate_time = currentTime;
          }
          // Call the existing update method
          this.updateGateTime(videoNumber, currentTime);
        }
      }
    },

    togglePlay() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (!video1 || !video2) return;

      // Check if both videos are fully loaded before allowing synchronized playback
      if (!this.videosLoaded[1] || !this.videosLoaded[2]) {
        console.log('Waiting for both videos to load before synchronized playback');
        return;
      }

      if (this.isPlaying) {
        // Pause both videos simultaneously
        video1.pause();
        video2.pause();
        this.isPlaying = false;
      } else {
        // Check if we need to apply sync (first play or after seeking)
        const needsSync = Math.abs(video1.currentTime - this.getExpectedVideo1Time()) > 0.1 || 
                         Math.abs(video2.currentTime - this.getExpectedVideo2Time()) > 0.1;
        
        if (needsSync) {
          // Apply sync offset first
          this.seekTo();
          
          // Small delay for seek completion, then play active videos
          setTimeout(() => {
            this.playActiveVideos();
          }, 50);
        } else {
          // Resume immediately from current positions
          this.playActiveVideos();
        }
        
        this.isPlaying = true;
      }
    },

    getExpectedVideo1Time() {
      if (!this.syncInstructions || !this.syncInstructions.delay_video) {
        return this.currentTime;
      }
      
      if (this.syncInstructions.delay_video === 1) {
        return Math.max(0, this.currentTime - this.syncInstructions.delay_seconds);
      } else {
        return this.currentTime;
      }
    },

    getExpectedVideo2Time() {
      if (!this.syncInstructions || !this.syncInstructions.delay_video) {
        return this.currentTime;
      }
      
      if (this.syncInstructions.delay_video === 2) {
        return Math.max(0, this.currentTime - this.syncInstructions.delay_seconds);
      } else {
        return this.currentTime;
      }
    },

    playActiveVideos() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      // Ensure both videos are loaded before attempting synchronized playback
      if (!this.videosLoaded[1] || !this.videosLoaded[2]) {
        console.log('Cannot play - waiting for both videos to load');
        return;
      }
      
      if (this.syncInstructions && this.syncInstructions.delay_video) {
        const delaySeconds = this.syncInstructions.delay_seconds;
        
        if (this.syncInstructions.delay_video === 1) {
          // Video 1 is delayed - only play if timeline is past delay
          if (this.currentTime >= delaySeconds) {
            video1.play().catch(e => console.log('Video 1 play failed:', e));
          }
          video2.play().catch(e => console.log('Video 2 play failed:', e)); // Video 2 always plays
        } else {
          // Video 2 is delayed - only play if timeline is past delay
          video1.play().catch(e => console.log('Video 1 play failed:', e)); // Video 1 always plays
          if (this.currentTime >= delaySeconds) {
            video2.play().catch(e => console.log('Video 2 play failed:', e));
          }
        }
      } else {
        // No delay, play both
        video1.play().catch(e => console.log('Video 1 play failed:', e));
        video2.play().catch(e => console.log('Video 2 play failed:', e));
      }
    },

    seekTo() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (!video1 || !video2) return;
      
      // Ensure both videos are loaded before seeking
      if (!this.videosLoaded[1] || !this.videosLoaded[2]) {
        console.log('Cannot seek - waiting for both videos to load');
        return;
      }

      if (this.syncInstructions && this.syncInstructions.delay_video) {
        const delaySeconds = this.syncInstructions.delay_seconds;
        
        if (this.syncInstructions.delay_video === 1) {
          // Video 1 is delayed
          const video1StartTime = this.currentTime - delaySeconds;
          
          if (video1StartTime >= 0) {
            // Video 1 should be playing
            video1.currentTime = video1StartTime;
            if (this.isPlaying) {
              video1.play().catch(e => console.log('Video 1 play failed:', e));
            }
          } else {
            // Video 1 should not be playing yet
            video1.currentTime = 0;
            video1.pause();
          }
          
          // Video 2 always plays normally
          video2.currentTime = this.currentTime;
          
        } else {
          // Video 2 is delayed
          const video2StartTime = this.currentTime - delaySeconds;
          
          if (video2StartTime >= 0) {
            // Video 2 should be playing
            video2.currentTime = video2StartTime;
            if (this.isPlaying) {
              video2.play().catch(e => console.log('Video 2 play failed:', e));
            }
          } else {
            // Video 2 should not be playing yet
            video2.currentTime = 0;
            video2.pause();
          }
          
          // Video 1 always plays normally
          video1.currentTime = this.currentTime;
        }
      } else {
        // No sync needed, both play normally
        video1.currentTime = this.currentTime;
        video2.currentTime = this.currentTime;
      }
    },

    setPlaybackRate() {
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (video1 && video2) {
        video1.playbackRate = this.playbackRate;
        video2.playbackRate = this.playbackRate;
      }
    },

    resetVideoPlayers() {
      this.isPlaying = false;
      this.currentTime = 0;
      this.duration = 0;
      this.playbackRate = 1;
      this.videosLoaded = { 1: false, 2: false };
      this.syncConfirmed = false;
      this.individualVideoStates = { 1: { isPlaying: false, currentTime: 0, duration: 0 }, 2: { isPlaying: false, currentTime: 0, duration: 0 } };
    },

    formatTime(seconds) {
      if (isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    confirmSync() {
      this.syncConfirmed = true;
      // Calculate sync instructions now
      if (this.currentComparison && this.currentComparison.sync_instructions) {
        this.syncInstructions = this.currentComparison.sync_instructions;
      }
      
      // Re-trigger video sizing for larger display when synced
      this.$nextTick(() => {
        if (this.videosLoaded[1]) {
          this.onIndividualVideoLoaded(1);
        }
        if (this.videosLoaded[2]) {
          this.onIndividualVideoLoaded(2);
        }
      });
    },

    resetSync() {
      this.syncConfirmed = false;
      this.syncInstructions = null;
      
      // Reset synchronized controls
      this.isPlaying = false;
      this.currentTime = 0;
      this.playbackRate = 1;
      
      // Reinitialize individual video states for existing videos
      const video1 = this.$refs.video1Player;
      const video2 = this.$refs.video2Player;
      
      if (video1) {
        this.individualVideoStates[1] = {
          isPlaying: false,
          currentTime: video1.currentTime || 0,
          duration: video1.duration || 0
        };
        video1.pause();
      }
      
      if (video2) {
        this.individualVideoStates[2] = {
          isPlaying: false,
          currentTime: video2.currentTime || 0,
          duration: video2.duration || 0
        };
        video2.pause();
      }
      
      console.log('Reset sync - individual video states:', this.individualVideoStates);
      
      // Re-trigger video sizing for smaller display when not synced
      this.$nextTick(() => {
        if (this.videosLoaded[1]) {
          this.onIndividualVideoLoaded(1);
        }
        if (this.videosLoaded[2]) {
          this.onIndividualVideoLoaded(2);
        }
      });
    },

    handleVideoClick(videoNumber) {
      // Prevent default fullscreen behavior and toggle play instead
      this.toggleIndividualPlay(videoNumber);
    },

    applyVideoSizing(videoNumber, video) {
      if (!video) return;

      // Square-cropped videos are sized entirely by CSS (aspect-ratio + cover);
      // clear any inline sizing left over from a previous full-frame layout.
      if (this.isSquare(videoNumber)) {
        video.style.height = '';
        video.style.width = '';
        video.style.margin = '';
        return;
      }

      const isMobile = window.innerWidth <= 768;
      const aspectRatio = video.videoWidth / video.videoHeight;
      const containerWidth = video.parentElement.clientWidth;
      const idealHeight = containerWidth / aspectRatio;

      let maxHeight;
      if (isMobile) {
        const screenHeight = window.innerHeight;
        const availableHeight = screenHeight - 200;

        if (this.syncConfirmed) {
          // Two videos stack vertically on mobile and must fit on screen with the
          // controls directly under the second one (no scrolling needed).
          maxHeight = screenHeight * (window.innerWidth <= 480 ? 0.3 : 0.34);
          if (aspectRatio < 1) {
            maxHeight = screenHeight * (window.innerWidth <= 480 ? 0.34 : 0.38);
          }
        } else {
          maxHeight = Math.min(availableHeight * 0.6, window.innerWidth <= 480 ? 400 : 450);
          if (aspectRatio < 1) {
            maxHeight = Math.min(availableHeight * 0.7, 520);
          }
        }
      } else {
        maxHeight = this.syncConfirmed ? 500 : 380;
      }

      if (idealHeight <= maxHeight) {
        video.style.height = idealHeight + 'px';
        video.style.width = '100%';
      } else {
        video.style.height = maxHeight + 'px';
        video.style.width = (maxHeight * aspectRatio) + 'px';
        if ((maxHeight * aspectRatio) < containerWidth) {
          video.style.margin = '0 auto';
          video.style.display = 'block';
        }
      }
    },

    // ---- Square crop (display-only) ----
    cropYFor(videoNumber) {
      if (this.cropEditing === videoNumber) return this.cropDraft;
      const saved = this.currentComparison && this.currentComparison[`video${videoNumber}_crop_y`];
      return saved === null || saved === undefined ? 50 : Number(saved);
    },

    isSquare(videoNumber) {
      if (this.cropEditing === videoNumber) return true;
      const saved = this.currentComparison && this.currentComparison[`video${videoNumber}_crop_y`];
      return saved !== null && saved !== undefined;
    },

    maybeStartCrop(videoNumber) {
      const saved = this.currentComparison && this.currentComparison[`video${videoNumber}_crop_y`];
      const alreadyCropped = saved !== null && saved !== undefined;
      if (this.isPortrait[videoNumber] && !alreadyCropped) {
        this.cropEditing = videoNumber;
        this.cropDraft = 50;
      }
    },

    editCrop(videoNumber) {
      this.cropEditing = videoNumber;
      this.cropDraft = this.cropYFor(videoNumber);
    },

    async confirmCrop() {
      const videoNumber = this.cropEditing;
      if (!videoNumber || !this.currentComparison) return;
      const value = Number(this.cropDraft);
      try {
        const token = localStorage.getItem('access_token');
        const updateData = {};
        updateData[`video${videoNumber}_crop_y`] = value;
        const response = await axios.patch(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/update/`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
      } catch (error) {
        console.error('Error saving crop:', error);
        alert('Failed to save crop');
      } finally {
        this.cropEditing = null;
        this.$nextTick(() => {
          this.applyVideoSizing(videoNumber, this.$refs[`video${videoNumber}Player`]);
        });
      }
    },

    cancelCrop() {
      // Keep the video uncropped (full frame) for this slot.
      const videoNumber = this.cropEditing;
      this.cropEditing = null;
      this.$nextTick(() => {
        this.applyVideoSizing(videoNumber, this.$refs[`video${videoNumber}Player`]);
      });
    },

    // ---- Zoom (display-only) for horizontal videos ----
    isZoomed(videoNumber) {
      if (this.zoomEditing === videoNumber) return true;
      const z = this.currentComparison && this.currentComparison[`video${videoNumber}_zoom`];
      return z !== null && z !== undefined && Number(z) > 1;
    },

    zoomFactorFor(videoNumber) {
      if (this.zoomEditing === videoNumber) return this.zoomDraft;
      const z = this.currentComparison && this.currentComparison[`video${videoNumber}_zoom`];
      return z === null || z === undefined ? 1 : Number(z);
    },

    zoomXFor(videoNumber) {
      if (this.zoomEditing === videoNumber) return this.zoomXDraft;
      const v = this.currentComparison && this.currentComparison[`video${videoNumber}_zoom_x`];
      return v === null || v === undefined ? 50 : Number(v);
    },

    zoomYFor(videoNumber) {
      if (this.zoomEditing === videoNumber) return this.zoomYDraft;
      const v = this.currentComparison && this.currentComparison[`video${videoNumber}_zoom_y`];
      return v === null || v === undefined ? 50 : Number(v);
    },

    videoStyle(videoNumber) {
      if (this.isSquare(videoNumber)) {
        return { objectPosition: `50% ${this.cropYFor(videoNumber)}%` };
      }
      if (this.isZoomed(videoNumber)) {
        return {
          transform: `scale(${this.zoomFactorFor(videoNumber)})`,
          transformOrigin: `${this.zoomXFor(videoNumber)}% ${this.zoomYFor(videoNumber)}%`,
        };
      }
      return {};
    },

    editZoom(videoNumber) {
      this.zoomEditing = videoNumber;
      this.zoomDraft = this.zoomFactorFor(videoNumber) > 1 ? this.zoomFactorFor(videoNumber) : 1.5;
      this.zoomXDraft = this.zoomXFor(videoNumber);
      this.zoomYDraft = this.zoomYFor(videoNumber);
    },

    async confirmZoom() {
      const videoNumber = this.zoomEditing;
      if (!videoNumber || !this.currentComparison) return;
      try {
        const token = localStorage.getItem('access_token');
        const updateData = {};
        updateData[`video${videoNumber}_zoom`] = Number(this.zoomDraft);
        updateData[`video${videoNumber}_zoom_x`] = Number(this.zoomXDraft);
        updateData[`video${videoNumber}_zoom_y`] = Number(this.zoomYDraft);
        const response = await axios.patch(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/update/`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
      } catch (error) {
        console.error('Error saving zoom:', error);
        alert('Failed to save zoom');
      } finally {
        this.zoomEditing = null;
      }
    },

    async removeZoom() {
      const videoNumber = this.zoomEditing;
      this.zoomEditing = null;
      if (!videoNumber || !this.currentComparison) return;
      try {
        const token = localStorage.getItem('access_token');
        const updateData = {};
        updateData[`video${videoNumber}_zoom`] = null;
        updateData[`video${videoNumber}_zoom_x`] = null;
        updateData[`video${videoNumber}_zoom_y`] = null;
        const response = await axios.patch(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/update/`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
      } catch (error) {
        console.error('Error removing zoom:', error);
      }
    },

    cancelZoom() {
      this.zoomEditing = null;
    },

    // ---- Overlay (ghost) view ----
    toggleOverlayMode() {
      this.viewMode = this.viewMode === 'overlay' ? 'side-by-side' : 'overlay';
      this.$nextTick(() => {
        if (this.videosLoaded[1]) this.applyVideoSizing(1, this.$refs.video1Player);
        if (this.videosLoaded[2]) this.applyVideoSizing(2, this.$refs.video2Player);
      });
    },

    // ---- In-app video picker ----
    openPicker(videoNumber) {
      this.picker.active = true;
      this.picker.videoNumber = videoNumber;
      this.picker.posts = [];
      this.picker.offset = 0;
      this.picker.hasMore = true;
      this.fetchPickerPosts();
    },

    closePicker() {
      this.picker.active = false;
      this.picker.videoNumber = null;
    },

    applyPickerFilters() {
      this.picker.posts = [];
      this.picker.offset = 0;
      this.picker.hasMore = true;
      this.fetchPickerPosts();
    },

    setPickerSource(mine) {
      if (this.picker.filters.mine === mine) return;
      this.picker.filters.mine = mine;
      this.applyPickerFilters();
    },

    async fetchPickerPosts() {
      if (this.picker.loading || !this.picker.hasMore) return;
      this.picker.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const params = { limit: this.picker.limit, offset: this.picker.offset };
        if (this.picker.filters.minSpeed) params.speed = this.picker.filters.minSpeed;
        if (this.picker.filters.minRope) params.rope = this.picker.filters.minRope;
        if (this.picker.filters.mine) params.mine = 'true';

        const response = await axios.get(`${API_BASE_URL}/api/post-list/`, {
          params,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const data = response.data || [];
        const withVideo = data.filter((p) => p.video);
        this.picker.posts = this.picker.posts.concat(withVideo);
        this.picker.offset += this.picker.limit;
        if (data.length < this.picker.limit) {
          this.picker.hasMore = false;
        }
      } catch (error) {
        console.error('Error loading videos:', error);
      } finally {
        this.picker.loading = false;
      }
    },

    authorName(post) {
      const a = post.author;
      if (!a) return '';
      const full = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
      return full || a.username || '';
    },

    onPickerVideoMeta(postId, event) {
      const v = event.target;
      if (v && v.videoWidth && v.videoHeight) {
        this.pickerOrientation = {
          ...this.pickerOrientation,
          [postId]: v.videoHeight > v.videoWidth ? 'vertical' : 'horizontal',
        };
      }
    },

    hoverPlay(event) {
      const video = event.currentTarget.querySelector('video');
      if (video) {
        try {
          video.currentTime = 0;
        } catch (e) { /* ignore */ }
        const playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(() => {});
      }
    },

    hoverPause(event) {
      const video = event.currentTarget.querySelector('video');
      if (video) {
        video.pause();
      }
    },

    async selectPostVideo(post) {
      const videoNumber = this.picker.videoNumber;
      if (!videoNumber || !this.currentComparison) return;
      this.loading = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(
          `${API_BASE_URL}/api/comparisons/${this.currentComparison.id}/attach-post-video/`,
          { post_id: post.id, video_number: videoNumber.toString() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.currentComparison = response.data;
        this.syncInstructions = response.data.sync_instructions;
        // A fresh video was attached: re-check orientation and offer cropping.
        this.pendingCropCheck[videoNumber] = true;
        this.videosLoaded[videoNumber] = false;
        this.closePicker();
      } catch (error) {
        console.error('Error selecting video:', error);
        alert('Failed to add this video to the comparison');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.compare-mode-container {
  --surface: #1a2632;
  --surface-2: #131e29;
  --surface-3: #223141;
  --border: #2c3a47;
  --accent: #1da1f2;
  --accent-hover: #4cb5f5;
  --text: #eef3f7;
  --text-strong: #ffffff;
  --muted: #aebcc8;
  --danger: #e1306c;

  max-width: 1100px;
  margin: 0 auto 80px;
  padding: 24px 20px;
  color: var(--text);
  text-align: left;
}

/* ---- List view header ---- */
.page-header {
  margin-bottom: 28px;
}

.page-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-header h2 {
  color: var(--text-strong);
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0;
}

.page-header p {
  color: var(--muted);
  font-size: 1rem;
  margin: 8px 0 0;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background-color: var(--surface-2);
  color: var(--text);
  font-size: 16px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--accent);
}

.form-input.small {
  width: auto;
  min-width: 140px;
}

/* ---- Buttons ---- */
.action-button {
  padding: 11px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.05s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.action-button:active {
  transform: translateY(1px);
}

.action-button.primary {
  background-color: var(--accent);
  color: #fff;
}

.action-button.primary:hover {
  background-color: var(--accent-hover);
}

.action-button.secondary {
  background-color: var(--surface-3);
  color: var(--text);
  border: 1px solid var(--border);
}

.action-button.secondary:hover {
  background-color: #2a3a49;
}

.action-button.danger {
  background-color: var(--danger);
  color: white;
}

.action-button.danger.ghost {
  background-color: transparent;
  color: var(--muted);
  border: 1px solid var(--border);
  padding: 10px 12px;
}

.action-button.danger.ghost:hover {
  background-color: rgba(225, 48, 108, 0.15);
  color: #ff6b9d;
  border-color: rgba(225, 48, 108, 0.4);
}

.action-button:disabled {
  background-color: var(--surface-3);
  color: #6b7884;
  cursor: not-allowed;
  border-color: transparent;
}

.action-button.small {
  padding: 7px 13px;
  font-size: 13px;
}

.create-btn {
  font-size: 15px;
}

/* ---- Comparisons list ---- */
.comparisons-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 30px;
}

.comparison-card {
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.comparison-card:hover {
  border-color: var(--accent);
  background-color: var(--surface-3);
}

.comparison-info h4 {
  color: var(--text-strong);
  margin: 0 0 4px;
  font-size: 1.15rem;
  font-weight: 700;
}

.comparison-info p {
  color: var(--muted);
  margin: 0 0 10px;
  font-size: 0.85rem;
}

.video-status {
  display: flex;
  gap: 8px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background-color: var(--surface-3);
  color: var(--muted);
}

.status-badge.complete {
  background-color: rgba(29, 161, 242, 0.18);
  color: #6cc4f7;
}

.comparison-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

/* ---- Empty state ---- */
.empty-state {
  text-align: center;
  color: var(--muted);
  padding: 60px 20px;
  border: 1px dashed var(--border);
  border-radius: 16px;
}

.empty-state i {
  font-size: 2.2rem;
  color: var(--surface-3);
  margin-bottom: 14px;
  display: block;
}

.empty-state p {
  margin: 0;
  font-size: 0.95rem;
}

/* ---- Comparison interface ---- */
.comparison-interface {
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 22px;
}

.comparison-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: none;
  color: var(--muted);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 8px;
  flex-shrink: 0;
}

.back-button:hover {
  color: var(--text-strong);
  background-color: var(--surface-3);
}

.comparison-title-input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 10px;
  color: var(--text-strong);
  font-size: 1.35rem;
  font-weight: 700;
  padding: 8px 12px;
}

.comparison-title-input::placeholder {
  color: #6b7884;
  font-weight: 600;
}

.comparison-title-input:hover {
  border-color: var(--border);
}

.comparison-title-input:focus {
  outline: none;
  border-color: var(--accent);
  background-color: var(--surface-2);
}

/* ---- View mode toggle (synced) ---- */
.view-mode-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.segmented {
  display: inline-flex;
  background-color: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
}

.segmented button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: 0.9rem;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.segmented button.active {
  background-color: var(--accent);
  color: #fff;
}

.blend-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.blend-control label {
  color: var(--muted);
  font-size: 0.9rem;
  font-weight: 600;
}

.blend-control input[type="range"] {
  width: 140px;
  accent-color: var(--accent);
}

/* ---- Scrub + gate controls ---- */
.gate-controls {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scrub-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.scrub-play {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: var(--accent);
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

.scrub-play:hover {
  background-color: var(--accent-hover);
}

.scrub-time {
  font-variant-numeric: tabular-nums;
  color: var(--text);
  font-size: 0.9rem;
  min-width: 42px;
}

.scrub-slider {
  flex: 1;
  accent-color: var(--accent);
  height: 6px;
}

.gate-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background-color: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 14px;
  transition: border-color 0.15s ease;
}

.gate-row.is-set {
  border-color: rgba(29, 161, 242, 0.5);
}

.gate-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--text);
  font-weight: 600;
  font-size: 0.9rem;
}

.gate-label i {
  color: var(--accent);
}

.gate-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px 10px;
}

.gate-value input {
  width: 80px;
  background: transparent;
  border: none;
  color: var(--text-strong);
  font-size: 1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.gate-value input:focus {
  outline: none;
}

.gate-unit {
  color: var(--muted);
  font-size: 0.85rem;
}

.set-gate-btn {
  margin-left: auto;
  background-color: rgba(29, 161, 242, 0.15);
  color: #6cc4f7;
  border: 1px solid rgba(29, 161, 242, 0.4);
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.set-gate-btn:hover {
  background-color: rgba(29, 161, 242, 0.28);
}

/* ---- Sticky sync action bar ---- */
.sync-bar {
  position: sticky;
  bottom: 14px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
  padding: 14px 18px;
  background-color: rgba(19, 30, 41, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.sync-bar-gates {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}

.sync-gate {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 0.9rem;
}

.sync-gate strong {
  color: #6b7884;
  font-variant-numeric: tabular-nums;
}

.sync-gate.set strong {
  color: #6cc4f7;
}

.sync-btn {
  font-size: 1rem;
  padding: 12px 24px;
}

.video-upload-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.video-upload-card {
  background-color: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.video-upload-card h4 {
  color: var(--muted);
  margin-bottom: 14px;
  text-align: left;
  flex-shrink: 0;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.upload-area {
  margin-bottom: 20px;
}

.upload-placeholder {
  border: 2px dashed #38444d;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.upload-placeholder:hover {
  border-color: #1da1f2;
}

.upload-placeholder i {
  font-size: 2rem;
  color: var(--muted);
  margin-bottom: 10px;
}

.upload-placeholder p {
  color: var(--muted);
  margin: 0;
}

.upload-progress {
  margin-top: 15px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #38444d;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background-color: #1da1f2;
  transition: width 0.2s ease;
}

.video-uploaded {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px;
  background-color: #15202b;
  border-radius: 12px;
  min-height: 150px;
}

.remove-video-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 5;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.95rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.remove-video-btn:hover {
  background-color: var(--danger);
}

/* Larger videos when synced */
.comparison-interface[data-synced="true"] .uploaded-video {
  max-height: 550px;
}

.comparison-interface[data-synced="true"] .video-uploaded {
  padding: 10px;
}

.comparison-interface[data-synced="true"] .video-upload-card {
  padding: 15px;
}

.video-uploaded .uploaded-video {
  margin-bottom: 15px;
}

.video-uploaded i {
  font-size: 2rem;
  margin-bottom: 10px;
}

.uploaded-video {
  max-width: 100%;
  height: auto;
  object-fit: contain;
  background-color: #000;
  border-radius: 8px;
  display: block;
  margin: 0 auto;
}

/* Clipping frame so zoomed videos don't spill over their card */
.video-frame {
  display: block;
  width: 100%;
  text-align: center;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;
}

/* Square crop for vertical videos (display-only) */
.uploaded-video.square-crop {
  width: 100%;
  max-width: 500px;
  aspect-ratio: 1 / 1;
  height: auto;
  object-fit: cover;
}

/* Zoom for horizontal videos (display-only) */
.uploaded-video.zoomed {
  transition: transform 0.05s linear;
}

/* Crop editor */
.crop-editor {
  width: 100%;
  max-width: 500px;
  margin: 12px auto 0;
  background-color: #0d161e;
  border: 1px solid #38444d;
  border-radius: 8px;
  padding: 12px;
}

.crop-hint {
  color: var(--muted);
  font-size: 0.85rem;
  margin: 0 0 10px;
  text-align: center;
}

.crop-slider-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.crop-slider-row span {
  color: var(--muted);
  font-size: 0.8rem;
}

.crop-slider {
  flex: 1;
  accent-color: #1da1f2;
}

.crop-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 12px;
}

.crop-toggle {
  margin-top: 10px;
}

/* Overlay (ghost) comparison view */
.video-upload-section.overlay-mode {
  display: block;
  position: relative;
  height: min(70vh, 600px);
  background-color: #000;
  border-radius: 12px;
  overflow: hidden;
}

.video-upload-section.overlay-mode .video-upload-card {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  background: transparent;
}

.video-upload-section.overlay-mode .video-uploaded,
.video-upload-section.overlay-mode .video-frame {
  width: 100%;
  height: 100%;
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Size both videos by height so a square-cropped clip stays square and both
   clips overlay at the same on-screen size, regardless of the box width. */
.video-upload-section.overlay-mode .uploaded-video {
  width: auto !important;
  height: 100% !important;
  max-width: 100%;
  max-height: 100%;
  margin: 0 auto;
  border-radius: 0;
}

.video-upload-section.overlay-mode .uploaded-video.square-crop {
  width: auto !important;
  height: 100% !important;
  aspect-ratio: 1 / 1;
}

.video-upload-section.overlay-mode .video-upload-card:last-child {
  z-index: 2;
  pointer-events: none;
}

.video-upload-section.overlay-mode .video-upload-card:last-child .uploaded-video {
  opacity: var(--overlay-opacity, 0.5);
  mix-blend-mode: screen;
}

/* View mode + overlay opacity controls */
.view-mode-control {
  display: flex;
  align-items: center;
}

.control-button.view-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #38444d;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.9rem;
}

.control-button.view-toggle.active {
  background-color: #1da1f2;
}

.overlay-opacity-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.overlay-opacity-control label {
  color: var(--muted);
  font-size: 0.85rem;
}

.overlay-opacity-slider {
  width: 120px;
  accent-color: #1da1f2;
}

/* In-app video picker modal */
.picker-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.picker-modal {
  background-color: #15202b;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #38444d;
}

.picker-header h3 {
  color: #ffffff;
  font-size: 1.2rem;
  margin: 0;
}

.picker-close {
  background: transparent;
  border: none;
  color: var(--muted);
  font-size: 1.2rem;
  cursor: pointer;
}

.picker-filters {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #38444d;
  flex-wrap: wrap;
  align-items: center;
}

.picker-source-toggle {
  display: inline-flex;
  background-color: #0d161e;
  border: 1px solid #38444d;
  border-radius: 999px;
  padding: 2px;
}

.picker-source-btn {
  border: none;
  background: transparent;
  color: var(--muted);
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.85rem;
}

.picker-source-btn.active {
  background-color: #1da1f2;
  color: #ffffff;
}

.picker-body {
  overflow-y: auto;
  padding: 16px;
}

.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.picker-more {
  display: block;
  margin: 16px auto 0;
}

.picker-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background-color: #000;
  border: 2px solid transparent;
  transition: border-color 0.15s ease;
}

.picker-item:hover {
  border-color: #1da1f2;
}

.picker-video {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
  background-color: #000;
}

.picker-play-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.55);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.picker-item:hover .picker-play-hint {
  opacity: 0;
}

.picker-author {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0));
  padding: 6px 8px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

.picker-author i {
  opacity: 0.8;
  font-size: 0.68rem;
  flex-shrink: 0;
}

.picker-meta {
  position: absolute;
  bottom: 6px;
  left: 6px;
  right: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.picker-badge {
  background-color: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
}

.picker-status {
  grid-column: 1 / -1;
  text-align: center;
  color: var(--muted);
  padding: 20px;
}

.upload-or {
  text-align: center;
  color: var(--muted);
  font-size: 0.8rem;
  margin: 10px 0;
}

.select-app-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.gate-time-input {
  display: flex;
  align-items: center;
  gap: 10px;
}

.gate-time-input label {
  color: #ffffff;
  font-size: 0.9rem;
  margin-bottom: 0;
}

.controls-section {
  margin-top: 22px;
}

.video-controls {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 18px;
  background-color: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.sync-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  background-color: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 16px;
  margin-top: 14px;
}

.sync-info p {
  margin: 0;
  color: var(--muted);
  font-size: 0.9rem;
}

.control-button {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background-color: var(--accent);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
}

.control-button:hover {
  background-color: var(--accent-hover);
}

.time-display {
  color: var(--text-strong);
  font-size: 0.95rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  min-width: 92px;
}

.progress-container {
  flex: 1;
}

.progress-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--surface-3);
  outline: none;
  cursor: pointer;
  accent-color: var(--accent);
}

.progress-slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--accent);
  cursor: pointer;
}

.speed-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.speed-control label {
  color: var(--muted);
  font-size: 0.9rem;
  font-weight: 600;
}

.speed-select {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--surface);
  color: var(--text);
  font-size: 0.9rem;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(21, 32, 43, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #38444d;
  border-top: 4px solid #1da1f2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-overlay p {
  color: #ffffff;
  font-size: 1.1rem;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .compare-mode-container {
    padding: 10px;
    /* Clear the fixed bottom navigation so the last card isn't hidden */
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }

  .page-header {
    margin-bottom: 20px;
  }

  .page-header h2 {
    font-size: 1.5rem;
    margin-bottom: 5px;
  }

  .page-header p {
    font-size: 1rem;
    margin-bottom: 10px;
  }

  .feature-credit {
    position: static;
    text-align: center;
    margin-top: 5px;
    font-size: 0.7rem;
  }

  /* Stack videos vertically on mobile (both before and after sync) for
     better visibility and easier top/bottom comparison. */
  .video-upload-section {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 20px;
  }

  .comparison-interface[data-synced="true"] .video-upload-section {
    grid-template-columns: 1fr;
    gap: 6px;
    margin-bottom: 6px;
  }

  /* Overlay mode keeps a single stacked box even on mobile. */
  .video-upload-section.overlay-mode,
  .comparison-interface[data-synced="true"] .video-upload-section.overlay-mode {
    display: block;
    grid-template-columns: none;
    height: min(60vh, 480px);
  }

  .video-upload-card {
    padding: 10px;
  }

  .comparison-interface[data-synced="true"] .video-upload-card {
    padding: 4px;
  }

  .video-upload-card h4 {
    font-size: 0.9rem;
    margin-bottom: 8px;
  }

  /* Optimize video container for larger videos */
  .video-uploaded {
    padding: 8px;
    background-color: #15202b;
    border-radius: 8px;
    min-height: auto;
  }

  .comparison-interface[data-synced="true"] .video-uploaded {
    padding: 3px;
  }

  /* Video sizing for mobile - much larger */
  .uploaded-video {
    max-height: 450px;
    max-width: 100%;
    width: 100%;
    object-fit: contain;
    border-radius: 6px;
  }

  /* Larger videos when synced to better use screen space */
  /* Keep both synced videos + controls on screen without scrolling */
  .comparison-interface[data-synced="true"] .uploaded-video {
    max-height: 34vh;
  }

  /* Mobile-friendly individual video controls */
  .individual-video-controls {
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
    background: #192734;
    border-radius: 8px;
    margin-top: 8px;
  }

  .individual-control-button {
    width: 40px;
    height: 40px;
    font-size: 0.8rem;
    min-touch-target: 44px;
  }

  .individual-control-button.set-gate {
    min-width: 75px;
    padding: 8px 10px;
    font-size: 0.7rem;
    height: 40px;
    margin-left: auto;
  }

  .individual-time-display {
    font-size: 0.8rem;
    padding: 6px 8px;
    background-color: rgba(21, 32, 43, 0.8);
  }

  .individual-progress-container {
    flex-basis: 100%;
    width: 100%;
    margin: 8px 0 0 0;
    order: 10;
  }

  .individual-progress-slider {
    height: 10px;
    border-radius: 5px;
  }

  .individual-progress-slider:hover {
    height: 12px;
  }

  .individual-progress-slider::-webkit-slider-thumb {
    width: 26px;
    height: 26px;
  }

  /* Gate time inputs */
  .gate-time-input {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    margin-top: 8px;
  }

  .gate-time-input label {
    font-size: 0.8rem;
  }

  .form-input.small {
    width: 100%;
    min-width: unset;
    font-size: 16px;
    padding: 10px 12px;
  }

  /* Synchronized controls for mobile */
  .controls-section {
    margin-top: 8px;
  }

  .video-controls {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px;
    margin-top: 8px;
  }

  .control-button {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }

  /* Compact layout: drag bar on top, then play + time + speed share one row */
  .progress-container {
    order: -1;
    flex-basis: 100%;
    margin: 0 0 2px;
  }

  .time-display {
    order: 1;
    font-size: 0.9rem;
    min-width: auto;
  }

  .progress-slider {
    height: 8px;
  }

  .progress-slider::-webkit-slider-thumb {
    width: 24px;
    height: 24px;
  }

  .speed-control {
    order: 2;
    margin-left: auto;
  }

  .speed-select {
    font-size: 16px;
    padding: 10px 15px;
    min-width: 80px;
  }

  /* New scrub + gate + sync components on mobile */
  .scrub-slider {
    height: 10px;
  }

  .gate-row {
    gap: 10px;
  }

  .set-gate-btn {
    margin-left: 0;
    width: 100%;
  }

  .sync-bar {
    flex-direction: column;
    align-items: stretch;
    bottom: 8px;
  }

  .sync-bar-gates {
    justify-content: space-between;
  }

  .sync-btn {
    width: 100%;
    justify-content: center;
  }

  .view-mode-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .segmented {
    width: 100%;
  }

  .segmented button {
    flex: 1;
    justify-content: center;
  }

  .blend-control {
    justify-content: space-between;
  }

  .comparison-title-input {
    font-size: 1.1rem;
  }

  /* Comparison cards */
  .comparison-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
  }

  .comparison-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .action-button {
    padding: 12px 20px;
    font-size: 16px;
    min-height: 44px;
  }

  /* Sync confirmation */
  .sync-confirmation-section {
    margin-top: 20px;
    padding: 15px;
  }

  .confirmation-card {
    padding: 12px;
  }

  .confirmation-card h4 {
    font-size: 1.2rem;
  }

  .gate-time-summary {
    text-align: left;
    margin: 12px 0;
  }

  .gate-time-item {
    margin-bottom: 6px;
    font-size: 0.85rem;
  }

  /* Upload areas */
  .upload-placeholder {
    padding: 25px 12px;
  }

  .upload-placeholder i {
    font-size: 1.4rem;
  }

  .upload-placeholder p {
    font-size: 0.85rem;
  }

  /* Form inputs */
  .form-input {
    font-size: 16px;
    padding: 12px 16px;
  }

  /* Sync info */
  .sync-info {
    padding: 10px;
    text-align: left;
    margin-top: 15px;
  }

  .sync-info p {
    font-size: 0.85rem;
    margin-bottom: 8px;
  }

  .action-button.small {
    padding: 6px 12px;
    font-size: 13px;
    margin-left: 0;
    margin-top: 6px;
  }

  /* Reduce header spacing */
  .comparison-header {
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  .comparison-header h3 {
    font-size: 1.3rem;
  }

  /* Optimize comparison interface padding */
  .comparison-interface {
    padding: 15px;
  }

  /* Loading indicator styles */
  .loading-indicator {
    text-align: center;
    padding: 20px;
    background-color: #15202b;
    border-radius: 12px;
    margin-bottom: 20px;
  }

  .loading-spinner {
    width: 30px;
    height: 30px;
    border: 3px solid #38444d;
    border-top: 3px solid #1da1f2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px auto;
  }

  .loading-indicator p {
    color: var(--muted);
    margin-bottom: 15px;
    font-size: 0.9rem;
  }

  .loading-status {
    display: flex;
    justify-content: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  .loading-status span {
    color: #e1306c;
    font-size: 0.8rem;
    padding: 4px 8px;
    border-radius: 4px;
    background-color: rgba(225, 48, 108, 0.1);
  }

  .loading-status span.loaded {
    color: #17bf63;
    background-color: rgba(23, 191, 99, 0.1);
  }

  /* Disabled controls styling */
  .video-controls.disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .control-button:disabled {
    background-color: #38444d;
    color: #657786;
    cursor: not-allowed;
  }

  .progress-slider:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .speed-select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: #657786;
  }
}

/* Extra small mobile devices */
@media (max-width: 480px) {
  .compare-mode-container {
    padding: 8px;
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }

  /* Even larger videos on small screens */
  .uploaded-video {
    max-height: 400px;
  }

  .comparison-interface[data-synced="true"] .uploaded-video {
    max-height: 30vh;
  }

  .individual-control-button.set-gate {
    min-width: 65px;
    font-size: 0.65rem;
    margin-left: auto;
  }

  .video-upload-card {
    padding: 8px;
  }

  .comparison-interface[data-synced="true"] .video-upload-card {
    padding: 6px;
  }

  .individual-video-controls {
    padding: 6px 8px;
  }

  .comparison-interface {
    padding: 12px;
  }

  .comparison-interface[data-synced="true"] .video-upload-section {
    gap: 6px;
  }

  .individual-progress-slider {
    height: 12px;
    border-radius: 6px;
  }

  .individual-progress-slider:hover {
    height: 14px;
  }

  .individual-progress-slider::-webkit-slider-thumb {
    width: 28px;
    height: 28px;
  }
}

@media (min-width: 769px) {
  .video-upload-section {
    align-items: start;
  }
  
  .video-upload-card {
    height: fit-content;
  }
}

.sync-confirmation-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #15202b;
  border-radius: 16px;
}

.confirmation-card {
  background-color: #192734;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.confirmation-card h4 {
  color: #1da1f2;
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.confirmation-card p {
  color: var(--muted);
  margin-bottom: 20px;
}

.gate-time-summary {
  margin-bottom: 20px;
}

.gate-time-item {
  margin-bottom: 10px;
}

.gate-time-item strong {
  color: #ffffff;
  font-weight: 500;
}

.gate-time-item span {
  color: var(--muted);
  font-weight: 400;
}

.gate-time-item .not-set {
  color: #e1306c;
}

.confirmation-actions {
  margin-top: 20px;
}

.individual-video-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #192734 0%, #1a2a39 100%);
  border-radius: 12px;
  border: 1px solid rgba(29, 161, 242, 0.1);
}

.individual-control-button {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 18px;
  background-color: #1da1f2;
  color: white;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(29, 161, 242, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.individual-control-button:hover {
  background-color: #1991db;
  transform: translateY(-0.5px);
  box-shadow: 0 2px 6px rgba(29, 161, 242, 0.3);
}

.individual-control-button.set-gate {
  width: auto;
  min-width: 90px;
  padding: 8px 14px;
  border-radius: 18px;
  font-size: 0.7rem;
  font-weight: 600;
  background-color: #17bf63;
  box-shadow: 0 1px 4px rgba(23, 191, 99, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin-left: auto;
}

.individual-control-button.set-gate:hover {
  background-color: #15a854;
  transform: translateY(-0.5px);
  box-shadow: 0 2px 6px rgba(23, 191, 99, 0.3);
}

.individual-time-display {
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  background-color: rgba(21, 32, 43, 0.6);
  padding: 6px 10px;
  border-radius: 6px;
}

.individual-progress-container {
  flex-basis: 100%;
  width: 100%;
  margin: 8px 0 0 0;
  order: 10;
}

.individual-progress-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(90deg, #38444d 0%, #4a5568 100%);
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.individual-progress-slider:hover {
  height: 10px;
}

.individual-progress-slider::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1da1f2 0%, #1991db 100%);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(29, 161, 242, 0.4);
  transition: all 0.2s ease;
}

.individual-progress-slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(29, 161, 242, 0.6);
}

.individual-control-button i {
  font-size: 0.7rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.individual-control-button.set-gate i {
  font-size: 0.7rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.individual-control-button .fas.fa-pause {
    margin-top: 10px;
}

.individual-control-button .fas.fa-play {
    margin-top: 10px;
}

.individual-control-button .fas.fa-stop {
    margin-top: 10px;
}
</style> 